import { Box, Button, Card, CardContent, Stack, Typography } from "@mui/material";
import CircularProgress from '@mui/material/CircularProgress';
import { push } from "connected-react-router";
import { useAuthState, useRecordContext, useSaveContext } from "ra-core";
import { FC, useEffect, useMemo, useState } from "react";
import { Helmet } from 'react-helmet-async';
import { useDispatch } from "react-redux";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import Edit from "~/controller/Edit";
import { useSimpleSaveAction } from "~/controller/SaveContextProvider";
import { RecPatch } from "~/dataProvider/tools";
import { DeviceFlowClient, DeviceFlowRequestInfo } from "~/gen/api/user";
import { useUserTimeFormat } from "~/utils/userTimeFormat";
import { removeDeviceFlowInfo, retrieveDeviceFlowInfo } from "./Device.common";
import LayoutAuth from "./LayoutAuth";


const DeviceConsent: FC = () => {
  const { authenticated } = useAuthState()
  const deviceFlow = useMemo(retrieveDeviceFlowInfo, [])
  // delay 0.2s to wait for resource 'device-consent' been registered
  const [delayed, setDelayed] = useState(false) 
  useEffect(() => {
    setTimeout(removeDeviceFlowInfo)
    setTimeout(() => setDelayed(true), 200)
  }, [])

  const handlers = useDefaultSavedHandlers("Device Consent")
  const dispatch = useDispatch()

  if (!deviceFlow || !authenticated) {
    dispatch(push('/login'))
    return null
  }
  return (
    !delayed ? null :
    <Edit resource="device-consent" basePath="" id={deviceFlow.usercode} {...handlers} mutationMode="pessimistic" >
      <DeviceConsentBody />
    </Edit>
  )
}

const DeviceConsentBody: FC = () => {
  const deviceConsentInfo = useRecordContext() as DeviceFlowRequestInfo&RecPatch
  const { saving } = useSaveContext()
  const giveConsent = useSimpleSaveAction()
  const format = useUserTimeFormat()
  const dispatch = useDispatch()
  const leave = () => {
    dispatch(push("/"))
  }

  const [done, setDone] = useState(false)

  const allowDevice = () => {
    giveConsent('device-consent' as any, {
      onSuccess: () => {
        setDone(true)
      },
    })
  }

  const needClientRegister = 
    deviceConsentInfo &&
    deviceConsentInfo.deviceFlowClient !== DeviceFlowClient.PSDownload && 
    deviceConsentInfo.deviceFlowClient !== DeviceFlowClient.PSUpload
  return (
    <LayoutAuth>
      <Helmet>
        <title>
          {needClientRegister 
            ? "Client Registration"
            : "Device Consent"
          } | OnData
        </title>
      </Helmet>
      <Card role="main">
        <CardContent sx={{ display: 'flex', flexDirection: 'column', p: 4 }} >
          <Stack justifyContent="space-between" alignItems="center" sx={{ mb: 3 }} >
            <div>
              <Typography
                id="sign-in-title"
                color="textPrimary"
                gutterBottom
                variant="h4"
                tabIndex={-1}
                component="h1"
              >
                {needClientRegister ? "Client Registration" : "Device Consent"}
              </Typography>
            </div>
          </Stack>
          <Box sx={{ flexGrow: 1 }} >
            {deviceConsentInfo 
            ? <>
                <Box>
                  {deviceConsentInfo.error
                  ? <>
                      <Typography variant="body2" color="error.main">{deviceConsentInfo.error}</Typography>
                      <Button
                        color="primary"
                        fullWidth
                        size="large"
                        onClick={() => leave()}
                        variant="outlined"
                      >
                        Leave
                      </Button>
                    </>
                  : <>
                      <Typography variant="caption">
                        Requested at: {" "}
                        <b>{format(deviceConsentInfo.creationTime || 0, 'PPp')}</b>
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 2 }} >
                        {deviceConsentInfo.deviceFlowClient && 
                          ClientsDescriptions[deviceConsentInfo.deviceFlowClient]}
                      </Typography>
                    </>
                  }
                </Box>
                <Box sx={{ mt: 2 }}>
                  {(!deviceConsentInfo.error && done)
                  ? <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
                      {needClientRegister
                        ? "Your consent to the client registration request has been received. You can now close this page and go back to the OnData Configurator to finish the client registration process. Once the client registration process is completed, the client has to be activated at the OnData Portal before it can be used."
                        : "Device Consent succeeded! You can now close this page."
                      }
                    </Typography>
                  : <Stack direction="row" spacing={4}>
                      <Button
                        color="primary"
                        fullWidth
                        size="large"
                        onClick={() => leave()}
                        variant="outlined"
                      >
                        No, Do Not Allow
                      </Button>
                      <Button
                        disabled={saving || done}
                        color="primary"
                        fullWidth
                        size="large"
                        onClick={allowDevice}
                        variant="contained"
                      >
                        Yes, Allow
                      </Button>
                    </Stack>
                  }
                </Box>
              </>
            : <Stack justifyContent="space-around">
                <CircularProgress size={32} sx={{ mx: 'auto' }} />
              </Stack>
            }
          </Box>
        </CardContent>
      </Card>
    </LayoutAuth>
  )
}

export default DeviceConsent;

const ClientsDescriptions = {
  [DeviceFlowClient.PSUpload]: 'An installation request of OnData Backup Script is asking for your permission.',
  [DeviceFlowClient.PSDownload]: 'A backup/encryption file download request from OnData Download Script is asking for your permission.',
  [DeviceFlowClient.DataDriver]: 'A client registration of the OnData Driver has been requested. Would you allow such request to be submitted on your behalf?',
  [DeviceFlowClient.EncryptionTool]:   'A client registration of the OnData Encryption Tool has been requested. Would you allow such request to be submitted on your behalf?',
  [DeviceFlowClient.EncryptionToolV2]: 'A client registration of the OnData Encryption Tool V2 has been requested. Would you allow such request to be submitted on your behalf?',
}
